import React, {useMemo, useState} from "react";
import {useSearchParams} from "react-router-dom";
import {useItemCategories} from "../hooks/useItemCategories";
import {useItems} from "../hooks/useItems";
import {buildCategoryTree} from "../utility/categoryTree";
import ShopFilters from "../components/ShopFilters";
import ItemCard from "../components/ItemCard";

const ShopPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);

    const itemCategoryId = searchParams.get("category") ? Number(searchParams.get("category")) : null;
    const page = searchParams.get("page") ? Number(searchParams.get("page")) : 1;
    const selectedPropertyValueIds = searchParams.getAll("pv").map(Number);

    const {itemCategories} = useItemCategories();
    const {items, pagination} = useItems(page, itemCategoryId, selectedPropertyValueIds);

    const categoryTree = useMemo(() => buildCategoryTree(itemCategories), [itemCategories]);

    const propertyGroups = useMemo(() => {
        const groups = new Map();

        items.forEach((item) => {
            item.variants.forEach((variant) => {
                variant.properties.forEach((property) => {
                    if (!groups.has(property.name)) {
                        groups.set(property.name, new Map());
                    }
                    groups.get(property.name).set(property.itemPropertyValueId, property.value);
                });
            });
        });

        return Array.from(groups.entries()).map(([name, valuesMap]) => ({
            name,
            values : Array.from(valuesMap.entries()).map(([itemPropertyValueId, value]) => ({itemPropertyValueId, value}))
        }));
    }, [items]);

    const activeFilterCount = (itemCategoryId ? 1 : 0) + selectedPropertyValueIds.length;

    const updateParams = (mutate) => {
        const next = new URLSearchParams(searchParams);
        mutate(next);
        setSearchParams(next);
    };

    const handleSelectCategory = (categoryId) => {
        updateParams((next) => {
            if (categoryId) {
                next.set("category", categoryId);
            } else {
                next.delete("category");
            }
            next.delete("page");
        });
    };

    const handleTogglePropertyValue = (itemPropertyValueId) => {
        updateParams((next) => {
            const current = next.getAll("pv").map(Number);
            next.delete("pv");
            const nextValues = current.includes(itemPropertyValueId)
                ? current.filter((id) => id !== itemPropertyValueId)
                : [...current, itemPropertyValueId];
            nextValues.forEach((id) => next.append("pv", id));
            next.delete("page");
        });
    };

    const handleResetFilters = () => {
        updateParams((next) => {
            next.delete("category");
            next.delete("pv");
            next.delete("page");
        });
    };

    const handlePageChange = (nextPage) => {
        updateParams((next) => {
            next.set("page", nextPage);
        });
    };

    return (
        <div className="max-w-6xl mx-auto p-4">
            <h1 className="text-2xl font-semibold text-gray-800 mb-6">Shop</h1>

            {/* Mobile filter toolbar */}
            <div className="md:hidden flex items-center justify-between mb-4">
                <button
                    onClick={() => setIsMobileFiltersOpen(true)}
                    className="flex items-center gap-2 border border-gray-300 rounded-lg px-4 py-2 text-sm font-semibold text-gray-700"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h18M6 12h12M10 20h4" />
                    </svg>
                    Filter
                    {activeFilterCount > 0 && (
                        <span className="bg-pegasusPurple text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                            {activeFilterCount}
                        </span>
                    )}
                </button>
                {activeFilterCount > 0 && (
                    <button onClick={handleResetFilters} className="text-sm text-pegasusPurple hover:underline">
                        Zurücksetzen
                    </button>
                )}
            </div>

            {/* Mobile filter bottom sheet */}
            <div
                className={`md:hidden fixed inset-0 bg-black z-40 transition-opacity duration-300 ${
                    isMobileFiltersOpen ? "bg-opacity-50 pointer-events-auto" : "bg-opacity-0 pointer-events-none"
                }`}
                onClick={() => setIsMobileFiltersOpen(false)}
            ></div>
            <div
                className={`md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-2xl shadow-lg max-h-[80vh] flex flex-col transition-transform duration-300 ${
                    isMobileFiltersOpen ? "translate-y-0" : "translate-y-full"
                }`}
            >
                <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
                    <h2 className="text-lg font-bold text-gray-800">Filter</h2>
                    <button
                        onClick={() => setIsMobileFiltersOpen(false)}
                        className="text-gray-500 hover:text-gray-700 text-xl leading-none"
                        aria-label="Filter schließen"
                    >
                        &#10005;
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto p-4">
                    <ShopFilters
                        categoryTree={categoryTree}
                        itemCategoryId={itemCategoryId}
                        onSelectCategory={(categoryId) => {
                            handleSelectCategory(categoryId);
                        }}
                        propertyGroups={propertyGroups}
                        selectedPropertyValueIds={selectedPropertyValueIds}
                        onTogglePropertyValue={handleTogglePropertyValue}
                    />
                </div>
                <div className="p-4 border-t flex-shrink-0">
                    <button
                        onClick={() => setIsMobileFiltersOpen(false)}
                        className="w-full bg-pegasusPurple text-white py-2 rounded-lg font-semibold hover:bg-pegasusPurpleHover"
                    >
                        Ergebnisse anzeigen{pagination ? ` (${pagination.total})` : ""}
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <aside className="hidden md:block md:col-span-1">
                    <ShopFilters
                        categoryTree={categoryTree}
                        itemCategoryId={itemCategoryId}
                        onSelectCategory={handleSelectCategory}
                        propertyGroups={propertyGroups}
                        selectedPropertyValueIds={selectedPropertyValueIds}
                        onTogglePropertyValue={handleTogglePropertyValue}
                    />
                </aside>

                <div className="md:col-span-3">
                    {items.length > 0 ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                            {items.map((item) => (
                                <ItemCard key={item.uuid} item={item}/>
                            ))}
                        </div>
                    ) : (
                        <p className="text-gray-500">Keine Produkte gefunden.</p>
                    )}

                    {pagination && pagination.totalPages > 1 && (
                        <div className="flex items-center justify-center gap-4 mt-6">
                            <button
                                onClick={() => handlePageChange(page - 1)}
                                disabled={page <= 1}
                                className={`px-4 py-2 rounded-lg ${page <= 1 ? "text-gray-400 cursor-not-allowed" : "text-pegasusPurple hover:underline"}`}
                            >
                                &lt; Zurück
                            </button>
                            <span className="text-sm text-gray-600">
                                Seite {pagination.currentPage} von {pagination.totalPages}
                            </span>
                            <button
                                onClick={() => handlePageChange(page + 1)}
                                disabled={page >= pagination.totalPages}
                                className={`px-4 py-2 rounded-lg ${page >= pagination.totalPages ? "text-gray-400 cursor-not-allowed" : "text-pegasusPurple hover:underline"}`}
                            >
                                Weiter &gt;
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ShopPage;
